import React, { useEffect, useState } from 'react';
import API from '../services/api';

// One-time welcome popup explaining the new-client request workflow to
// therapists. Dismissal is per-user and server-side (announcement_dismissals),
// with localStorage as a fast cache — same rails as AnnouncementBanner.
// When the walkthrough video exists, it slots in above the steps.
const INTRO_ID = 'intake-flow-intro-2026-07-02';
const lsKey = `announcement_dismissed_${INTRO_ID}`;

export default function IntakeFlowIntroModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(lsKey) === '1') return undefined;
    let active = true;
    API.get('/therapist/announcement-dismissals')
      .then((res) => {
        if (!active) return;
        const ids = (res.data && res.data.ids) || [];
        if (ids.includes(INTRO_ID)) {
          localStorage.setItem(lsKey, '1');
        } else {
          setOpen(true);
        }
      })
      .catch(() => { if (active) setOpen(true); /* fall back to localStorage-only */ });
    return () => { active = false; };
  }, []);

  if (!open) return null;

  const dismiss = () => {
    localStorage.setItem(lsKey, '1');
    setOpen(false);
    API.post('/therapist/announcement-dismissals', { id: INTRO_ID }).catch(() => { /* best-effort */ });
  };

  return (
    <div style={overlayStyle}>
      <div style={boxStyle}>
        <div style={{ textAlign: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: 34 }}>🗂️</div>
          <h2 style={{ margin: '4px 0 2px', fontSize: 20 }}>New-client requests, handled your way</h2>
          <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            A quick tour of what's new on your dashboard — 30 seconds, once.
          </div>
        </div>

        <div style={stepStyle}>
          <span style={numStyle}>1</span>
          <div>
            <strong>A banner appears</strong> when someone books a new-client appointment.
            It stays at the top of your dashboard until you decide — nothing slips by.
          </div>
        </div>
        <div style={stepStyle}>
          <span style={numStyle}>2</span>
          <div>
            <strong>Click Review</strong> to see everything they submitted: appointment time,
            reason for seeking care, insurance, and notes.
          </div>
        </div>
        <div style={stepStyle}>
          <span style={numStyle}>3</span>
          <div>
            <strong>Pick an outcome:</strong>
            <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span>✓ <strong>Accept</strong> — they get a confirmation email, the appointment is marked Confirmed in Acuity, and you can create their client profile in one click.</span>
              <span>？ <strong>Request more info</strong> — the office follows up with your questions, or you note that you'll reach out yourself.</span>
              <span>✕ <strong>Reject</strong> — cancels the appointment in Acuity (the client gets the standard cancellation notice); your reason goes to the office only.</span>
              <span>💤 <strong>Not now</strong> — closes the popup; the banner waits for you.</span>
            </div>
          </div>
        </div>
        <div style={stepStyle}>
          <span style={numStyle}>4</span>
          <div>
            <strong>The office sees it all</strong> — every request, status, and note appears on
            their dashboard automatically. Deciding in the popup <em>is</em> the handoff; there's
            nothing to forward.
          </div>
        </div>

        <button onClick={dismiss} style={ctaStyle}>
          Got it — let's go
        </button>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16,
};
const boxStyle = {
  background: 'var(--white)', borderRadius: 16, padding: '22px 24px', width: '100%',
  maxWidth: 560, maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 16px 48px rgba(0,0,0,0.35)',
};
const stepStyle = {
  display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 0',
  borderBottom: '1px solid var(--surface-muted)', fontSize: 14, lineHeight: 1.5,
};
const numStyle = {
  flexShrink: 0, width: 26, height: 26, borderRadius: '50%', background: 'var(--primary-color)',
  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontWeight: 700, fontSize: 13, marginTop: 2,
};
const ctaStyle = {
  marginTop: 16, width: '100%', background: 'var(--accent-color)', color: '#fff',
  border: 'none', borderRadius: 10, padding: '12px 16px', fontWeight: 700, fontSize: 15,
  cursor: 'pointer',
};
